import React, { useState } from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { toast } from "react-toastify";
import "./StudentsTable.css";
import DeleteStudentConfirmationModal from "../Modals/DeleteStudentConfirmationModal/DeleteStudentConfirmationModal";
import StudentComprehensiveReportModal from "../../Reporting/Modals/StudentComprehensiveReportModal/StudentComprehensiveReportModal";
import useAxios from "../../../hooks/useAxios";
import ConfirmationModal from "../../common/ConfirmationModal/ConfirmationModal";

const StudentsTable = ({ students, onRefresh }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const axiosInstance = useAxios();
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [copiedCode, setCopiedCode] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  
  // Comprehensive report states
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentReports, setStudentReports] = useState({});
  const [generatingReports, setGeneratingReports] = useState({});
  const [loadingReports, setLoadingReports] = useState({});
  const [deleteReportModalOpen, setDeleteReportModalOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [deletingReport, setDeletingReport] = useState(false);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCopyLoginCode = async (loginCode) => {
    try {
      await navigator.clipboard.writeText(loginCode);
      setCopiedCode(loginCode);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const handleDeleteClick = (student) => {
    setStudentToDelete(student);
    setDeleteModalOpen(true);
  };

  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
    setStudentToDelete(null);
  };

  const handleStudentDeleted = () => {
    // Refresh the table after deletion
    if (onRefresh) {
      onRefresh();
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'suspended':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  // Slice data for pagination
  const paginatedStudents = students.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (students.length === 0) {
    return (
      <Box className="no-students-container">
        <Typography variant="h6" className="no-students-text">
          No students created yet
        </Typography>
        <Typography variant="body2" className="no-students-subtext">
          Create your first student account or import students from a file to get started.
        </Typography>
      </Box>
    );
  }

  console.log(students);

  // Fetch comprehensive reports for a student
  const fetchStudentReports = async (studentId) => {
    if (studentReports[studentId]) return; // Already loaded
    
    setLoadingReports(prev => ({ ...prev, [studentId]: true }));
    try {
      const response = await axiosInstance.get(`/reporting/students/${studentId}/comprehensive-report`);
      
      if (response.data.success) {
        // Backend now returns an array of reports
        const reports = response.data.reports || [];
        
        setStudentReports(prev => ({
          ...prev,
          [studentId]: reports
        }));
      } else {
        setStudentReports(prev => ({
          ...prev,
          [studentId]: []
        }));
      }
    } catch (err) {
      console.error('Failed to fetch student reports:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      
      // Handle specific error cases
      if (err.response?.status === 404) {
        // No reports found - this is normal for new students
        setStudentReports(prev => ({
          ...prev,
          [studentId]: []
        }));
      } else if (err.response?.status === 403) {
        // Authorization error
        console.error('Not authorized to view student reports');
        setStudentReports(prev => ({
          ...prev,
          [studentId]: []
        }));
      } else {
        // Other errors
        setStudentReports(prev => ({
          ...prev,
          [studentId]: []
        }));
      }
    } finally {
      setLoadingReports(prev => ({ ...prev, [studentId]: false }));
    }
  };

  // Generate new comprehensive report for a student
  const generateStudentReport = async (studentId) => {
    setGeneratingReports(prev => ({ ...prev, [studentId]: true }));
    try {
      const response = await axiosInstance.post(`/ai-reports/students/${studentId}/generate-comprehensive-report`);
      if (response.data.success) {
        // Add the new report to the list
        setStudentReports(prev => ({
          ...prev,
          [studentId]: [
            response.data.report,
            ...(prev[studentId] || [])
          ]
        }));
        
        toast.success('Student comprehensive report generated successfully!');
        console.log('Report generated successfully');
      } else {
        console.error('Failed to generate report:', response.data.message);
        // Show error message to user
        toast.error(`Failed to generate report: ${response.data.message}`);
      }
    } catch (err) {
      console.error('Failed to generate report:', err);
      
      // Handle specific error cases and show user-friendly messages
      if (err.response?.status === 404) {
        const errorMessage = err.response.data?.message || 'Student has no completed sessions to analyze.';
        toast.error(`Cannot generate report: ${errorMessage}`);
      } else if (err.response?.status === 403) {
        toast.error('You are not authorized to generate reports for this student.');
      } else if (err.response?.status >= 500) {
        toast.error('Server error occurred while generating the report. Please try again later.');
      } else {
        toast.error(`Failed to generate report: ${err.response?.data?.message || 'An unexpected error occurred.'}`);
      }
    } finally {
      setGeneratingReports(prev => ({ ...prev, [studentId]: false }));
    }
  };

  // Handle opening the comprehensive report modal
  const handleOpenComprehensiveReport = async (student) => {
    setSelectedStudent(student);
    await fetchStudentReports(student.id);
    setIsReportModalOpen(true);
  };

  // Handle closing the comprehensive report modal
  const handleCloseComprehensiveReport = () => {
    setIsReportModalOpen(false);
    setSelectedStudent(null);
  };

  // Handle delete report
  const handleDeleteReport = (report) => {
    setReportToDelete(report);
    setDeleteReportModalOpen(true);
  };

  const handleCloseDeleteReportModal = () => {
    setDeleteReportModalOpen(false);
    setReportToDelete(null);
  };

  const handleConfirmDeleteReport = async () => {
    if (!reportToDelete || !selectedStudent) return;
    
    setDeletingReport(true);
    try {
      const response = await axiosInstance.delete(`/reporting/students/${selectedStudent.id}/comprehensive-report/${reportToDelete.id}`);
      if (response.data.success) {
        toast.success('Report deleted successfully!');
        // Remove the deleted report from the list
        setStudentReports(prev => ({
          ...prev,
          [selectedStudent.id]: prev[selectedStudent.id]?.filter(r => r.id !== reportToDelete.id) || []
        }));
        handleCloseDeleteReportModal();
      } else {
        toast.error(response.data.message || 'Failed to delete report.');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'An error occurred while deleting the report.');
    } finally {
      setDeletingReport(false);
    }
  };

  return (
    <Box className="students-table-container">
      <Paper elevation={3} className="students-table-paper">
        <TableContainer>
          <Table aria-label="students table">
            <TableHead className="table-header">
              <TableRow>
                <TableCell className="header-cell">Student Name</TableCell>
                {/* {!isSmallScreen && <TableCell className="header-cell">Email</TableCell>} */}
                <TableCell className="header-cell">Login Code</TableCell>
                {!isSmallScreen && <TableCell className="header-cell">Status</TableCell>}
                {!isSmallScreen && <TableCell className="header-cell">Created Date</TableCell>}
                <TableCell className="header-cell">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedStudents.map((student) => (
                <TableRow key={student.id} className="table-row">
                  <TableCell className="student-name-cell">
                    <Typography variant="body1" className="student-name">
                      {student.student_profile?.full_name || 'N/A'}
                    </Typography>
                  </TableCell>
                  
                  {/* {!isSmallScreen && (
                    <TableCell className="email-cell">
                      <Typography variant="body2" className="email-text">
                        {student.email}
                      </Typography>
                    </TableCell>
                  )} */}
                  
                  <TableCell className="login-code-cell">
                    <Box className="login-code-container">
                      <Chip 
                        label={student.login_code} 
                        className="login-code-chip"
                        size="small"
                      />
                      <Tooltip title={copiedCode === student.login_code ? "Copied!" : "Copy login code"}>
                        <IconButton
                          size="small"
                          onClick={() => handleCopyLoginCode(student.login_code)}
                          className="copy-button"
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  
                  {!isSmallScreen && (
                    <TableCell className="status-cell">
                      <Chip
                        label={getStatusLabel(student.status)}
                        color={getStatusColor(student.status)}
                        size="small"
                        className="status-chip"
                      />
                    </TableCell>
                  )}
                  
                  {!isSmallScreen && (
                    <TableCell className="date-cell">
                      <Typography variant="body2" className="date-text">
                        {formatDate(student.created_at)}
                      </Typography>
                    </TableCell>
                  )}
                  
                  <TableCell className="actions-cell">
                    <Box className="actions-container">
                      <Tooltip title="View comprehensive report">
                        <IconButton
                          size="small"
                          className="view-report-button"
                          onClick={() => handleOpenComprehensiveReport(student)}
                        >
                          <AssessmentIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete student account">
                        <IconButton
                          size="small"
                          className="student-delete-button"
                          onClick={() => handleDeleteClick(student)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={students.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Rows per page:"
          className="table-pagination"
        />
      </Paper>
      
      {/* Delete Confirmation Modal */}
      <DeleteStudentConfirmationModal
        open={deleteModalOpen}
        onClose={handleDeleteModalClose}
        onConfirm={handleStudentDeleted}
        student={studentToDelete}
      />

      {/* Delete Report Confirmation Modal */}
      <ConfirmationModal
        open={deleteReportModalOpen}
        onClose={handleCloseDeleteReportModal}
        isDanger={true}
        title="Delete Comprehensive Report"
        subtitle={`Are you sure you want to delete this report?`}
        onSubmit={handleConfirmDeleteReport}
        submitText={deletingReport ? "Deleting..." : "Delete Report"}
        hasNotes={false}
      >
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          This action cannot be undone. The report will be permanently removed.
        </Typography>
      </ConfirmationModal>

      {/* Comprehensive Report Modal */}
      <StudentComprehensiveReportModal
        open={isReportModalOpen}
        onClose={handleCloseComprehensiveReport}
        student={selectedStudent}
        reports={studentReports[selectedStudent?.id] || []}
        loading={generatingReports[selectedStudent?.id] || false}
        onGenerateReport={() => generateStudentReport(selectedStudent?.id)}
        onDeleteReport={handleDeleteReport}
      />
    </Box>
  );
};

export default StudentsTable; 